/* ── Egenskaper för ett IFC-objekt, grupperade för inspektören ──
 * Identitet, en grupp per egenskapsuppsättning (Pset_*), Mängder (IfcElementQuantity)
 * och Material. Motoroberoende: tar emot objektdata där attribut är `{ value }`-omslag,
 * IFC-typen ligger i `_category` och objektets id i `_localId` (That Open Fragments).
 * Äldre form (`type`-sifferkod, `expressID`) hanteras inte längre.
 */

/** Packar upp värden ({value, type}) till text. Tomt → ''. */
export function plainValue(v) {
  if (v == null) return '';
  if (typeof v === 'object' && !Array.isArray(v) && 'value' in v) return plainValue(v.value);
  if (Array.isArray(v)) return v.map(plainValue).filter((x) => x !== '').join(', ');
  if (typeof v === 'boolean') return v ? 'Ja' : 'Nej';
  if (typeof v === 'number') return Number.isInteger(v) ? String(v) : String(Math.round(v * 1e6) / 1e6);
  if (typeof v === 'object') return '';
  const s = String(v);
  if (s === 'T' || s === '.T.') return 'Ja';
  if (s === 'F' || s === '.F.') return 'Nej';
  return s;
}

/** IFC-typ i versaler (t.ex. "IFCWALL"), eller ''. */
export function categoryOf(obj) {
  const c = obj?._category?.value ?? obj?._category;
  return typeof c === 'string' ? c.toUpperCase() : '';
}

/** Objektets id (IFC:s ExpressID), eller null. */
export function localIdOf(obj) {
  const n = Number(obj?._localId?.value ?? obj?._localId);
  return Number.isFinite(n) ? n : null;
}

function row(k, v) {
  const val = plainValue(v);
  return val === '' ? null : { k, v: val };
}

function asList(v) { return Array.isArray(v) ? v : []; }

/** En egenskap i en Pset: enkelt värde, uppräkning, lista eller intervall. */
function propertyRow(p) {
  const name = plainValue(p?.Name);
  if (!name) return null;
  let v = '';
  if (p.NominalValue != null) v = plainValue(p.NominalValue);
  else if (p.EnumerationValues != null) v = plainValue(p.EnumerationValues);
  else if (p.ListValues != null) v = plainValue(p.ListValues);
  else if (p.LowerBoundValue != null || p.UpperBoundValue != null) {
    v = [plainValue(p.LowerBoundValue), plainValue(p.UpperBoundValue)].filter(Boolean).join(' – ');
  }
  return v === '' ? null : { k: name, v };
}

const QUANTITY_KEYS = [
  ['LengthValue', 'length'], ['AreaValue', 'area'], ['VolumeValue', 'volume'],
  ['CountValue', 'count'], ['WeightValue', 'weight'], ['TimeValue', 'time'],
];

/** Enhetssymbol för en mängd. `units` är skalan mot SI per slag (t.ex. { length: 0.001 }). */
export function unitSymbol(kind, units) {
  const scale = units?.[kind];
  if (kind === 'count') return 'st';
  if (kind === 'time') return 's';
  if (scale == null) return '';
  const near = (a, b) => Math.abs(a - b) <= Math.abs(b) * 1e-6;
  if (kind === 'length') return near(scale, 1) ? 'm' : near(scale, 0.001) ? 'mm' : near(scale, 0.01) ? 'cm' : '';
  if (kind === 'area') return near(scale, 1) ? 'm²' : near(scale, 1e-6) ? 'mm²' : '';
  if (kind === 'volume') return near(scale, 1) ? 'm³' : near(scale, 1e-9) ? 'mm³' : '';
  return '';
}

function quantityRow(q, units, prefix) {
  const name = plainValue(q?.Name);
  if (!name) return null;
  for (const [key, kind] of QUANTITY_KEYS) {
    if (q[key] == null) continue;
    const val = plainValue(q[key]);
    if (val === '') return null;
    const unit = unitSymbol(kind, units);
    return { k: prefix ? `${prefix} · ${name}` : name, v: unit ? `${val} ${unit}` : val };
  }
  return null;
}

/** Hittar alla IfcMaterial i materialträdet (lager, listor, beståndsdelar). Tål cykler. */
function collectMaterials(mats) {
  const out = [];
  const seen = new Set();
  const visited = new WeakSet();
  (function walk(obj, depth) {
    if (!obj || typeof obj !== 'object' || depth > 8 || visited.has(obj)) return;
    visited.add(obj);
    if (Array.isArray(obj)) { obj.forEach((o) => walk(o, depth + 1)); return; }
    const t = categoryOf(obj);
    if (t === 'IFCMATERIAL') {
      const name = plainValue(obj.Name);
      const key = localIdOf(obj) ?? name;
      if (name && !seen.has(key)) { seen.add(key); out.push({ name, category: plainValue(obj.Category) }); }
      return;
    }
    if (t === 'IFCMATERIALLAYER') {
      const mat = Array.isArray(obj.Material) ? obj.Material[0] : obj.Material;
      const name = plainValue(mat?.Name);
      if (name) {
        out.push({ name, thickness: plainValue(obj.LayerThickness) });
        seen.add(localIdOf(mat) ?? name);
        return;
      }
    }
    for (const [k, v] of Object.entries(obj)) {
      if (k.startsWith('_') || k === 'AssociatedTo') continue;
      if (v && typeof v === 'object') walk(v, depth + 1);
    }
  })(mats, 0);
  return out;
}

/**
 * Bygger grupperna som inspektören visar.
 * @param {{ item: object, units?: object }} data `item` är objektets data med relationerna
 *   IsDefinedBy (egenskapsuppsättningar, mängder, typobjekt) och HasAssociations (material).
 * @returns {Array<{key: string, title: string, rows: Array<{k: string, v: string}>}>}
 */
export function buildPropertyGroups({ item, units }) {
  const props = item || {};
  const groups = [];

  const identity = [
    row('Namn', props.Name),
    row('Typ', categoryOf(props)),
    row('GlobalId', props._guid),
    row('Littera/Tag', props.Tag),
    row('Beskrivning', props.Description),
    row('Objekttyp', props.ObjectType),
    row('Långt namn', props.LongName),
    (() => { const p = plainValue(props.PredefinedType); return p && p !== 'NOTDEFINED' ? { k: 'Fördefinierad typ', v: p } : null; })(),
    row('ExpressID', localIdOf(props)),
  ].filter(Boolean);
  groups.push({ key: 'identity', title: 'Identitet', rows: identity });

  const defs = asList(props.IsDefinedBy);
  const psets = defs.filter((d) => Array.isArray(d?.HasProperties));
  const qtySets = defs.filter((d) => Array.isArray(d?.Quantities));
  // Typobjekt (IfcWallType m.fl.) bär sina egna egenskapsuppsättningar i HasPropertySets.
  const typeObjs = defs.filter((d) => Array.isArray(d?.HasPropertySets));

  const addSet = (set, suffix) => {
    const rows = asList(set?.HasProperties).map(propertyRow).filter(Boolean);
    const title = plainValue(set?.Name) || 'Egenskaper';
    if (rows.length) groups.push({ key: `pset-${localIdOf(set) ?? title}${suffix}`, title: suffix ? `${title} (typ)` : title, rows });
  };
  psets.forEach((s) => addSet(s, ''));
  typeObjs.forEach((tp) => asList(tp.HasPropertySets).forEach((s) => addSet(s, '-typ')));

  const qtyRows = [];
  for (const s of qtySets) {
    const prefix = qtySets.length > 1 ? plainValue(s.Name) : '';
    s.Quantities.forEach((q) => { const r = quantityRow(q, units, prefix); if (r) qtyRows.push(r); });
  }
  if (qtyRows.length) groups.push({ key: 'quantities', title: 'Mängder', rows: qtyRows });

  const materials = collectMaterials(props.HasAssociations);
  if (materials.length) {
    groups.push({
      key: 'materials',
      title: 'Material',
      rows: materials.map((m, i) => ({ k: materials.length > 1 ? `Material ${i + 1}` : 'Material', v: [m.name, m.thickness ? `${m.thickness} ${unitSymbol('length', units)}`.trim() : '', m.category].filter(Boolean).join(' · ') })),
    });
  }
  return groups;
}
